import * as React from "react"
import * as CommonUtils from "../../utils"
import * as Conf from "../../../configuration"
import { reduceThemeProjectAmounts } from "../../../../ppi/ppi-detail/utils/investments-helper"
import * as PpiDomain from "../../../../../domain/sfp/ppi-domain"
import * as Model from "../../../../../model"

const PREFIX_COLUMNS_COUNT = 3

export default class PpiThemeTable extends React.Component<Props> {
	private readonly valueColumnWidth: number

	constructor(props: Readonly<Props>) {
		super(props)
		this.valueColumnWidth = (props.width
		- props.labelColumnWidth
		- props.fctvaColumnWidth
		- props.priorityColumnWidth) / props.ppiYears.length
	}

	render() {
		const id = this.props.id
		const fctvaColumnWidth = this.props.fctvaColumnWidth
		const labelColumnWidth = this.props.labelColumnWidth
		const priorityColumnWidth = this.props.priorityColumnWidth
		const ppi = this.props.ppi
		const ppiYears = this.props.ppiYears
		const startYear = this.props.startYear
		const tableConf = this.props.tableConfiguration
		const theme = this.props.theme
		const totals = reduceThemeProjectAmounts(theme, ppi, startYear, [])

		return <table id={id}>
			<thead>
				<tr>
					<th data-pptx-width={labelColumnWidth}
					style={{ ...tableConf.header }}>
						Sous-thème / Projet
					</th>

					<th data-pptx-width={priorityColumnWidth}
					style={{ ...tableConf.header }}>
						Priorité
					</th>

					<th data-pptx-width={fctvaColumnWidth}
					style={{ ...tableConf.header }}>
						FCTVA
					</th>

					{ppiYears.map((ppiYear, yearIndex) =>
					<th data-pptx-width={this.valueColumnWidth}
					key={yearIndex}
					style={{ ...tableConf.header }}>
						{ppiYear.year}
					</th>)}
				</tr>
			</thead>

			<tbody>
				{theme.projects.map((project, projectIndex) =>
				this.createProjectRow(project, ppiYears, projectIndex, tableConf))}

				{this.createSubThemeRows(theme, ppi, ppiYears, startYear, tableConf)}
			</tbody>

			<tfoot>
				<tr>
					<th colSpan={PREFIX_COLUMNS_COUNT} style={{ ...tableConf.total }}>
						Total
					</th>

					{totals.map((amount, amountIndex) =>
					<th key={amountIndex}
					style={{ ...tableConf.total, ...tableConf.numeric }}>
						{CommonUtils.formatCurrency(
							this.props.locale,
							this.props.currency,
							amount,
							this.props.roundness
						)}
					</th>)}
				</tr>
			</tfoot>
		</table>
	}

	private createFooterRow(
		theme: Model.PpiInvestmentTheme,
		ppi: Model.Ppi,
		startYear: number,
		key: number,
		tableConf: Conf.TableConfiguration
	): React.ReactNode {
		const amounts = reduceThemeProjectAmounts(theme, ppi, startYear, [])

		return <tr key={key}>
			<th colSpan={PREFIX_COLUMNS_COUNT} style={{ ...tableConf.total }}>
				Total "{theme.name}"
			</th>

			{amounts.map((amount, amountIndex) =>
			<th key={amountIndex} style={{ ...tableConf.total, ...tableConf.numeric}}>
				{CommonUtils.formatCurrency(
					this.props.locale, this.props.currency, amount, this.props.roundness)}
			</th>)}
		</tr>
	}

	private createHeaderRow(
		theme: Model.PpiInvestmentTheme,
		ppiYears: Model.PpiYear[],
		key: number,
		tableConf: Conf.TableConfiguration
	): React.ReactNode {
		return <tr key={key}>
			<th colSpan={PREFIX_COLUMNS_COUNT + ppiYears.length}
			style={{ ...tableConf.header }}>{theme.name}</th>
		</tr>
	}

	private createProjectRow(
		project: Model.PpiInvestmentProject,
		ppiYears: Model.PpiYear[],
		key: number,
		tableConf: Conf.TableConfiguration
	): React.ReactNode {
		return <tr key={key}>
			<td style={{ ...tableConf.common }}>{project.name}</td>
			<td style={{ ...tableConf.common, ...tableConf.numeric }}>
				{project.priority}
			</td>
			<td style={{ ...tableConf.common }}>{project.fctva ? "Oui" : "Non"}</td>
			{ppiYears.map((ppiYear, yearIndex) => {
				const projectYear = PpiDomain.findProjectYearByYear(
					project.years, ppiYear.year)
				const amount = projectYear ? projectYear.amount || 0 : 0
				return <td key={yearIndex}
				style={{ ...tableConf.common, ...tableConf.numeric }}>
					{CommonUtils.formatCurrency(
						this.props.locale,
						this.props.currency,
						amount,
						this.props.roundness
					)}
				</td>
			})}
		</tr>
	}

	private createSubThemeRows(
		theme: Model.PpiInvestmentTheme,
		ppi: Model.Ppi,
		ppiYears: Model.PpiYear[],
		startYear: number,
		tableConf: Conf.TableConfiguration,
		firstRowIndex = 0
	): React.ReactNode {
		let rows: React.ReactNode[] = []
		let rowIndex = firstRowIndex

		for (const subTheme of theme.themes) {
			rows.push(this.createHeaderRow(subTheme, ppiYears, rowIndex++, tableConf))

			for (const project of subTheme.projects)
				project.visible && rows.push(this.createProjectRow(
					project, ppiYears, rowIndex++, tableConf))

			rows = rows.concat(this.createSubThemeRows(
				subTheme, ppi, ppiYears, startYear, tableConf, rowIndex++))

			rows.push(this.createFooterRow(
				subTheme, ppi, startYear, rowIndex++, tableConf))
		}

		return rows
	}
}

export interface Props {
	currency: string
	id: string
	fctvaColumnWidth: number
	labelColumnWidth: number
	locale: string
	ppi: Model.Ppi
	ppiYears: Model.PpiYear[]
	priorityColumnWidth: number
	roundness: Conf.RoundnessValue
	enableHideZeroLines: boolean
	startYear: number
	tableConfiguration: Conf.TableConfiguration
	theme: Model.PpiInvestmentTheme
	width: number
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/ppi-generator/ppi-theme-table/index.tsx